import "./OtherSummaryPanel.scss";

import { useEffect, useState } from "react";
import AdvancedTextArea from "components/inputs/TextArea/AdvancedTextArea";
import SummaryEmptyState from "./SummaryEmptyState";

type SectionData = {
  value: string;
  date: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onDateChange?: (value: string) => void;
  onDateBlur?: () => void;
};

type OtherSummarySection = {
  key: string;
  title: string;
  data: SectionData;
};

type OtherSummaryPanelProps = {
  acceptedWarnings: SectionData;
  deniedWarnings: SectionData;
  acceptedCostMitigations: SectionData;
  deniedCostMitigations: SectionData;
  emptyMessage: string;
  sourceContext?: {
    contextTitle: string;
    contextValue: string;
  };
  isEditable?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
};

const OtherSummaryPanel = ({
  acceptedWarnings,
  deniedWarnings,
  acceptedCostMitigations,
  deniedCostMitigations,
  emptyMessage,
  sourceContext,
  isEditable = true,
  isCollapsed = false,
  onToggleCollapse,
}: OtherSummaryPanelProps) => {
  const hasContent = !!(
    acceptedWarnings.value ||
    deniedWarnings.value ||
    acceptedCostMitigations.value ||
    deniedCostMitigations.value
  );
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const sections: OtherSummarySection[] = [
    {
      key: "warnings-accepted",
      title: "Warnings (Accepted)",
      data: acceptedWarnings,
    },
    {
      key: "warnings-denied",
      title: "Warnings (Denied)",
      data: deniedWarnings,
    },
    {
      key: "cost-mitigations-accepted",
      title: "Cost Mitigations (Accepted)",
      data: acceptedCostMitigations,
    },
    {
      key: "cost-mitigations-denied",
      title: "Cost Mitigations (Denied)",
      data: deniedCostMitigations,
    },
  ];

  return (
    <div
      className={`metadoc-ai-summary other-summary-panel ${isVisible ? "metadoc-ai-summary--visible" : ""} ${isCollapsed ? "other-summary-panel--collapsed" : ""}`}
    >
      {!hasContent ? (
        <SummaryEmptyState message={emptyMessage} />
      ) : (
        <>
          <div className="metadoc-ai-summary__header">
            <div className="metadoc-ai-summary__badge">
              <span className="material-icons">auto_awesome</span>
              <span className="metadoc-ai-summary__badge-text">
                AI Generated Summary
              </span>
            </div>
            <div className="metadoc-ai-summary__header-actions">
              {sourceContext && (
                <div className="metadoc-ai-summary__context">
                  <span className="metadoc-ai-summary__context-title">
                    {sourceContext.contextTitle}
                  </span>
                  <span className="metadoc-ai-summary__context-value">
                    {sourceContext.contextValue}
                  </span>
                </div>
              )}
              <button
                className="metadoc-ai-summary__collapse-btn"
                onClick={() => onToggleCollapse?.()}
                title={isCollapsed ? "Expand summary" : "Collapse summary"}
              >
                <span className="material-icons">
                  {isCollapsed ? "expand_more" : "expand_less"}
                </span>
              </button>
            </div>
          </div>

          {!isCollapsed && (
            <div className="other-summary-panel__content">
              {sections.map(
                ({ key, title, data }) =>
                  data.value && (
                    <div key={key} className="other-summary-panel__section">
                      <div className="other-summary-panel__section-header">
                        <h3 className="other-summary-panel__section-title">
                          {title}
                        </h3>
                        <div className="metadoc-ai-summary__context other-summary-panel__date-context">
                          <span className="metadoc-ai-summary__context-title">
                            Date
                          </span>
                          <input
                            type="date"
                            className="other-summary-panel__date-input"
                            value={data.date}
                            onChange={(e) =>
                              data.onDateChange?.(e.target.value)
                            }
                            onBlur={data.onDateBlur}
                            disabled={!isEditable}
                          />
                        </div>
                      </div>
                      <div className="other-summary-panel__section-content">
                        <AdvancedTextArea
                          value={data.value}
                          name={`other-summary-${key}`}
                          onChange={data.onChange ?? (() => {})}
                          onBlur={data.onBlur}
                          isEditable={isEditable}
                        />
                      </div>
                    </div>
                  ),
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OtherSummaryPanel;
